import React, { useEffect, useMemo, useState } from 'react';
import { dashboardApi } from '../../../services/api';
import {
  PatientStatusData,
  DrillDownContext,
  ClinicId,
  StageData,
  SideData,
  Segment,
  SegmentKey,
  segmentKey,
  Side,
  StageId,
} from './types';
import { getClinicConfig } from './clinicPalette';
import ClinicPills from './parts/ClinicPills';
import StageRow from './parts/StageRow';
import ClinicFilter from './parts/ClinicFilter';
import './styles.css';

const fmt = (n: number) => n.toLocaleString('en-US');

// Lifecycle category each operational stage rolls up into (Ohad's 4-category
// model, 2026-06-14). We keep the operational stages as-is and only print a
// category header above the first stage of each group — no bars are merged.
//   פרי  = new + intake_cm   ·  חדש = intake_psy
//   פעיל = followup_cm + followup_psy   ·  סיים = its own bottom section
type CatKey = 'pre' | 'new' | 'active' | 'done';

const STAGE_TO_CATEGORY: Record<string, CatKey> = {
  new: 'pre',
  intake_cm: 'pre',
  intake_psy: 'new',
  followup_cm: 'active',
  followup_psy: 'active',
};

// Label + one-line definition (shown as an ⓘ tooltip) + accent color per
// lifecycle category. Definitions per Ohad 2026-06-15.
const CATEGORY_INFO: Record<CatKey, { label: string; desc: string; color: string }> = {
  pre:    { label: 'מטופלים פרי',          color: '#92400E', desc: 'כל המטופלים שמילאו שאלון ועד לסיום אינטייק מנהל תיק' },
  new:    { label: 'מטופלים חדשים',        color: '#2563EB', desc: 'כל המטופלים בין סיום אינטייק מנהל תיק ועד לסיום אינטייק פסיכיאטר' },
  active: { label: 'מטופלים פעילים',       color: '#047857', desc: 'מסיום אינטייק פסיכיאטר ועד לסיום התהליך' },
  done:   { label: 'מטופלים שסיימו תהליך', color: '#8B5CF6', desc: 'כל המטופלים שסיימו את התהליך' },
};

function CatHeader({ catKey }: { catKey: CatKey }) {
  const info = CATEGORY_INFO[catKey];
  return (
    <div className="ps-cat-header">
      <span className="ps-cat-accent" style={{ background: info.color }} />
      <span className="ps-cat-label">{info.label}</span>
      <span className="ps-cat-desc">— {info.desc}</span>
    </div>
  );
}

// Bottom "done" section — one long bar of all finished patients, split by clinic.
function DoneSection({ finished, segments }: { finished: number; segments: Segment[] }) {
  const segs = segments.filter((s) => s.count > 0);
  const total = segs.reduce((acc, s) => acc + s.count, 0) || 1;
  return (
    <>
      <CatHeader catKey="done" />
      <div className="ps-done-row">
        <div className="ps-done-bar">
          {segs.map((s) => {
            const pct = (s.count / total) * 100;
            const cfg = getClinicConfig(s.clinic);
            return (
              <div
                key={s.clinic}
                className="ps-done-seg"
                style={{ width: `${pct}%`, background: cfg.hex }}
                title={`${cfg.displayName}: ${s.count.toLocaleString('en-US')} (${pct.toFixed(0)}%)`}
              >
                {pct >= 6 && <span className="ps-done-seg-val">{s.count.toLocaleString('en-US')}</span>}
              </div>
            );
          })}
        </div>
        <div className="ps-done-total num-eng">{finished.toLocaleString('en-US')}</div>
      </div>
    </>
  );
}

interface Props {
  onDrillDown?: (ctx: DrillDownContext) => void;
}

function filterSide(side: SideData, selected: Set<ClinicId>): SideData {
  const filtered = side.segments.filter((s) => selected.has(s.clinic));
  const count = filtered.reduce((acc, s) => acc + s.count, 0);
  // Recompute the side's average waiting_time as a weighted mean of the
  // filtered clinics. Without this the "X ימים..." text stays at the
  // all-clinic average even after the user narrows the filter.
  let time = side.time;
  if (side.time) {
    if (count === 0) {
      time = null;
    } else {
      const weightedSum = filtered.reduce(
        (acc, s) => acc + (s.avg_wait ?? 0) * s.count,
        0,
      );
      const value = Math.round(weightedSum / count);
      time = {
        ...side.time,
        value,
        text: side.time.text_template.replace('{v}', String(value)),
      };
    }
  }
  return { ...side, count, time, segments: filtered };
}

function filterStage(stage: StageData, selected: Set<ClinicId>, totalActive: number): StageData {
  const right = filterSide(stage.right, selected);
  const left = filterSide(stage.left, selected);
  const total = right.count + left.count;
  return {
    ...stage,
    total,
    pct_of_active: totalActive > 0 ? Math.round((total / totalActive) * 1000) / 10 : 0,
    right,
    left,
  };
}

// The five journey stages, in the order the loaded widget draws them — the skeleton
// has to match or the page visibly reshuffles the moment the data lands.
const SKELETON_STAGES = ['חדשים', 'אינטייק מנהל תיק', 'אינטייק פסיכיאטר', 'מעקב מנהל תיק', 'מעקב פסיכיאטר'];

export default function PatientStatus({ onDrillDown }: Props) {
  const [data, setData] = useState<PatientStatusData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<ClinicId> | null>(null);
  const [selectedSegments, setSelectedSegments] = useState<Set<SegmentKey>>(new Set());

  useEffect(() => {
    dashboardApi
      .getPatientStatus()
      .then((res) => {
        const d: PatientStatusData = res.data;
        setData(d);
        setSelected(new Set(d.clinics.map((c) => c.id)));
      })
      .catch((e) => setError(e?.message ?? 'Failed to load'));
  }, []);

  const { effectiveTotalActive, effectiveFinished, filteredStages, maxSideCount, stageFilterActive, stageFilteredClinics, stageFilteredTotal } = useMemo(() => {
    if (!data || !selected) {
      return {
        effectiveTotalActive: 0,
        effectiveFinished: 0,
        filteredStages: [] as StageData[],
        maxSideCount: 0,
        stageFilterActive: false,
        stageFilteredClinics: [] as { id: ClinicId; count: number; pct: number }[],
        stageFilteredTotal: 0,
      };
    }
    const isFiltering = selected.size < data.clinics.length;
    const totalActive = isFiltering
      ? data.clinics.filter((c) => selected.has(c.id)).reduce((acc, c) => acc + c.count, 0)
      : data.total_active;
    const finished = isFiltering
      ? data.finished_segments.filter((s) => selected.has(s.clinic)).reduce((acc, s) => acc + s.count, 0)
      : data.finished;
    const stages = isFiltering
      ? data.stages.map((s) => filterStage(s, selected, totalActive))
      : data.stages;
    // Per Shachar: every butterfly bar across all stages should be scaled
    // against the same reference — the largest single-side count globally
    // (e.g. Stage 3 left "תור נקבע" at ~1,667). Cross-stage comparison
    // becomes meaningful: a small stage has small bars, a big stage fills
    // its column.
    const maxSide = stages.reduce(
      (acc, s) => Math.max(acc, s.right.count, s.left.count),
      0,
    );

    // Stage-filter aggregation: when one or more (stage, side) cells are
    // selected, recompute the headline + clinic distribution from those
    // cells only. Composes with the clinic filter — segments are already
    // pre-filtered by clinic in `stages` above.
    const stageFilterOn = selectedSegments.size > 0;
    let sfTotal = 0;
    const sfPerClinic = new Map<ClinicId, number>();
    if (stageFilterOn) {
      for (const stage of stages) {
        const sides: Array<['right' | 'left', SideData]> = [['right', stage.right], ['left', stage.left]];
        for (const [sideKey, sideData] of sides) {
          if (!selectedSegments.has(segmentKey(stage.id as StageId, sideKey as Side))) continue;
          for (const seg of sideData.segments) {
            sfPerClinic.set(seg.clinic, (sfPerClinic.get(seg.clinic) ?? 0) + seg.count);
            sfTotal += seg.count;
          }
        }
      }
    }
    const sfClinics = data.clinics
      .map((c) => {
        const cnt = sfPerClinic.get(c.id) ?? 0;
        return {
          id: c.id,
          count: cnt,
          pct: sfTotal > 0 ? Math.round((cnt / sfTotal) * 1000) / 10 : 0,
        };
      });
    return {
      effectiveTotalActive: totalActive,
      effectiveFinished: finished,
      filteredStages: stages,
      maxSideCount: maxSide,
      stageFilterActive: stageFilterOn,
      stageFilteredClinics: sfClinics,
      stageFilteredTotal: sfTotal,
    };
  }, [data, selected, selectedSegments]);

  if (error) {
    return (
      <div className="patient-status-widget">
        <p className="ps-status">שגיאה בטעינה: {error}</p>
      </div>
    );
  }
  // While the numbers are in flight, draw the widget it is about to become rather
  // than a bare "טוען..." line: header, the five stage rows, both sides. The query
  // behind this page is the slowest one we serve, and a page that looks empty for
  // several seconds reads as broken (Ohad 2026-08-12 — "כמו דשבורד הנהלה").
  if (!data || !selected) {
    return (
      <div className="patient-status-widget" aria-busy="true">
        <div className="widget-header">
          <div className="widget-title-group">
            <h2 className="widget-title">מטופלים</h2>
            <span className="ps-sk ps-sk-num" />
          </div>
        </div>
        {SKELETON_STAGES.map((label) => (
          <div className="ps-sk-row" key={label}>
            <div className="ps-sk-label">{label}</div>
            <div className="ps-sk-bars">
              <span className="ps-sk ps-sk-bar" />
              <span className="ps-sk ps-sk-bar" />
            </div>
          </div>
        ))}
        <p className="ps-status">טוען נתונים…</p>
      </div>
    );
  }

  const handlePillClick = (clinic: ClinicId) => {
    const allIds = data.clinics.map((c) => c.id);
    if (selected.size === 1 && selected.has(clinic)) {
      setSelected(new Set(allIds));
    } else {
      setSelected(new Set([clinic]));
    }
  };

  const toggleSegment = (stage: StageId, side: Side) => {
    const k = segmentKey(stage, side);
    setSelectedSegments((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k); else next.add(k);
      return next;
    });
  };

  const clearStageFilter = () => setSelectedSegments(new Set());

  // When stage filter is active, the clinic pills should reflect the
  // distribution within the selected (stage, side) cells. Build a synthetic
  // clinics array for that case — same shape, but counts/pcts come from
  // the aggregated stage-filter data computed in the memo above.
  const pillsClinics = stageFilterActive
    ? data.clinics.map((c) => {
        const sf = stageFilteredClinics.find((s) => s.id === c.id);
        return { ...c, count: sf?.count ?? 0, pct: sf?.pct ?? 0 };
      })
    : data.clinics;

  return (
    <div className="patient-status-widget">
      <div className="widget-header">
        <div className="widget-title-group">
          <h2 className="widget-title">מטופלים</h2>
          <span className="big-num num-eng">
            {fmt(stageFilterActive ? stageFilteredTotal : effectiveTotalActive)}
          </span>
          <span className="info-icon" title="כל מטופל שמילא שאלון ולא נמצא בסטטוס סיים">ⓘ</span>
        </div>
        <div className="widget-filter-group">
          <span className="title-date num-eng">{data.as_of_date}</span>
          <ClinicFilter clinics={data.clinics} selected={selected} onApply={setSelected} />
        </div>
      </div>

      {stageFilterActive && (
        <div className="stage-filter-banner">
          <span className="sfb-text">
            <span className="num-eng">{selectedSegments.size}</span> שלבים נבחרו · סה״כ{' '}
            <span className="num-eng">{fmt(stageFilteredTotal)}</span> מטופלים
          </span>
          <button type="button" className="sfb-clear" onClick={clearStageFilter}>
            נקה בחירה
          </button>
        </div>
      )}

      {stageFilterActive && stageFilteredTotal === 0 && (
        <div className="cohort-empty">
          <p>אין מטופלים שעונים על שילוב השלבים והקופות שנבחר.</p>
        </div>
      )}

      <ClinicPills
        clinics={pillsClinics}
        selected={selected}
        onClinicClick={handlePillClick}
      />

      {(() => {
        const out: React.ReactNode[] = [];
        let lastCat = '';
        for (const stage of filteredStages) {
          const catKey = STAGE_TO_CATEGORY[stage.id];
          if (catKey && catKey !== lastCat) {
            out.push(<CatHeader key={`cat-${catKey}`} catKey={catKey} />);
            lastCat = catKey;
          }
          out.push(
            <StageRow
              key={stage.id}
              stage={stage}
              referenceTotal={maxSideCount}
              selectedSides={{
                right: selectedSegments.has(segmentKey(stage.id as StageId, 'right')),
                left: selectedSegments.has(segmentKey(stage.id as StageId, 'left')),
              }}
              onSideClick={(side) => onDrillDown?.({ stage: stage.id, side })}
              onSideToggle={(side) => toggleSegment(stage.id as StageId, side)}
            />,
          );
        }
        return out;
      })()}

      {!stageFilterActive && (
        <DoneSection
          finished={effectiveFinished}
          segments={data.finished_segments.filter((s) => selected.has(s.clinic))}
        />
      )}
    </div>
  );
}
